import { RefObject, useCallback, useEffect } from 'react'
import { getCycledIndex, runWhenInDom, useCaptureKeydown } from './useMenuKeyboardNav'

/** Selector for focusable main-menu items in the action-bar popover. */
const MAIN_MENU_ITEM_SELECTOR = '[data-main-menu-item][tabindex="0"]'
const NAV_KEYS = ['ArrowDown', 'ArrowUp', 'Tab']

/**
 * Keyboard navigation for the main action-bar popover menu.
 *
 * While the popover is open this hook:
 * - auto-focuses the first enabled menu item as soon as it appears in the DOM
 *   (MUI v4 portals mount asynchronously — no timeout polling needed),
 * - activates the focused item on Enter or Space (items carry role="button"),
 * - cycles focus with ArrowUp/ArrowDown and Tab/Shift+Tab, wrapping at both
 *   ends of the item list,
 * - stays silent whenever a submenu (Tasks / BP / Manage Options / Move Block)
 *   is open so the submenu's own key handling wins.
 *
 * @param isActionPopoverDisplayed Whether the main menu popover is open.
 * @param anySubmenuOpenRef Ref flag set to true while any submenu is displayed.
 */
export const useMainMenuKeyboardNav = (
    isActionPopoverDisplayed: boolean,
    anySubmenuOpenRef: RefObject<boolean>
): void => {
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (anySubmenuOpenRef.current) return

            const active = document.activeElement as HTMLElement
            const closest = active.closest?.(
                '[data-main-menu-item]'
            ) as HTMLElement | null
            if (!closest) return

            // ARIA: role="button" items must activate on both Enter and Space
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                closest.click()
                return
            }

            if (!NAV_KEYS.includes(e.key)) return
            e.preventDefault()
            e.stopPropagation()

            const items = Array.from(
                document.querySelectorAll<HTMLElement>(MAIN_MENU_ITEM_SELECTOR)
            )
            if (!items.length) return
            const currentIdx = items.indexOf(closest)
            const goBack =
                e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)
            items[getCycledIndex(currentIdx, items.length, goBack)].focus()
        },
        [anySubmenuOpenRef]
    )

    useCaptureKeydown(isActionPopoverDisplayed, handleKeyDown)

    useEffect(() => {
        if (!isActionPopoverDisplayed) return undefined
        return runWhenInDom(() => {
            const first = document.querySelector<HTMLElement>(
                MAIN_MENU_ITEM_SELECTOR
            )
            if (!first) return false
            first.focus()
            return true
        })
    }, [isActionPopoverDisplayed])
}
